// -----------------------------------------------------------------------------
// ErrorBoundary.jsx — site-wide React error boundary with a branded Lone Star
// Counsel fallback panel and a Spanish-language toggle (Texas is bilingual).
// If any downstream component throws during render, we swap the tree for a
// calm, on-brand recovery panel with a Return-to-Home CTA.
//
// - Fires a `render_error_boundary` GA4 event (via lib/analytics.js) with
//   error name, message, path, and component-stack head — for monitoring.
// - Detects initial language from browser preference; user can toggle.
// - Technical detail is written to console.error (visible in devtools & error
//   monitoring), but never surfaced to visitors.
// -----------------------------------------------------------------------------
import { Component } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const COPY = {
  en: {
    firmLine1: "LONE STAR \u2605 COUNSEL",
    firmLine2: "Attorneys \u2007\u007C\u2007 Counselors \u2007\u007C\u2007 Business Advisors",
    headA: "We're sorry \u2014",
    headB: "this page is temporarily unavailable.",
    body:
      "Our team has been notified. Please return to the home page or contact the firm directly for immediate assistance.",
    home: "Return to Home",
    call: "or call (972) 712-1515",
    dba: "a dba of Pratt Law Group, PLLC",
    otherLang: "Espa\u00f1ol",
  },
  es: {
    firmLine1: "LONE STAR \u2605 COUNSEL",
    firmLine2: "Abogados \u2007\u007C\u2007 Consejeros \u2007\u007C\u2007 Asesores Empresariales",
    headA: "Lo sentimos \u2014",
    headB: "esta p\u00e1gina no est\u00e1 disponible temporalmente.",
    body:
      "Nuestro equipo ha sido notificado. Por favor, regrese a la p\u00e1gina principal o comun\u00edquese con la firma directamente para asistencia inmediata.",
    home: "Volver al Inicio",
    call: "o llame al (972) 712-1515",
    dba: "una dba de Pratt Law Group, PLLC",
    otherLang: "English",
  },
};

function detectInitialLang() {
  if (typeof navigator === "undefined") return "en";
  const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase();
  return raw.startsWith("es") ? "es" : "en";
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, lang: detectInitialLang() };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log for engineers and any error monitor picking up console.error.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] uncaught render error:", error, info?.componentStack);

    // Emit a GA4 event so the firm can monitor how often the fallback is
    // shown in production. No PII — only the error class/message and a
    // truncated component-stack head that identifies the offending component.
    try {
      trackEvent("render_error_boundary", {
        error_name: error?.name || "Error",
        error_message: (error?.message || "").slice(0, 240),
        component_stack_head: (info?.componentStack || "").split("\n").slice(0, 3).join(" | ").slice(0, 240),
        path: typeof window !== "undefined" ? window.location.pathname : "",
      });
    } catch {
      // trackEvent is best-effort; never let analytics failures re-throw.
    }
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  handleToggleLang = () => {
    this.setState((s) => ({ lang: s.lang === "en" ? "es" : "en" }));
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const t = COPY[this.state.lang];

    return (
      <div
        className="min-h-screen bg-ivory grain flex flex-col items-center justify-center px-6 py-24"
        data-testid="error-boundary-fallback"
        lang={this.state.lang}
      >
        <div className="max-w-xl w-full text-center">
          <div className="text-brass tracking-[0.42em] uppercase text-[10px] font-medium">
            {t.firmLine1}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-navy/60">
            {t.firmLine2}
          </div>
          <h1 className="mt-10 font-display text-navy text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
            {t.headA}{" "}
            <span className="italic font-cormorant text-oxblood">{t.headB}</span>
          </h1>
          <p className="mt-8 text-navy/80 font-serif text-base md:text-lg leading-relaxed">
            {t.body}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/"
              onClick={this.handleReset}
              className="btn-primary"
              data-testid="error-boundary-home-link"
            >
              {t.home}
            </Link>
            <a
              href="tel:9727121515"
              className="text-[11px] uppercase tracking-[0.32em] text-navy hover:text-oxblood transition-colors font-medium border-b border-brass/50 hover:border-oxblood pb-1"
              data-testid="error-boundary-call-link"
            >
              {t.call}
            </a>
          </div>
          <button
            type="button"
            onClick={this.handleToggleLang}
            className="mt-10 text-[10px] uppercase tracking-[0.36em] text-navy/60 hover:text-oxblood transition-colors font-medium border-b border-brass/30 hover:border-oxblood pb-0.5"
            data-testid="error-boundary-lang-toggle"
          >
            {t.otherLang}
          </button>
          <div className="mt-10 text-[10px] uppercase tracking-[0.28em] text-navy/50">
            {t.dba}
          </div>
        </div>
      </div>
    );
  }
}
